import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authApi } from '../services/auth';
import { userOrganisationsApi } from '../services/userOrganisations';

export const checkAuth = createAsyncThunk(
    'auth/checkAuth',
    async (_, { rejectWithValue }) => {
        try {
            const accessToken = localStorage.getItem('access_token');
            if (!accessToken) throw new Error('No token');

            const user_id = localStorage.getItem('user_id');
            const role = localStorage.getItem('user_role'); // Читаем исходную роль из localStorage
            const firstName = localStorage.getItem('first_name');
            const lastName = localStorage.getItem('last_name');

            await authApi.getProtectedData();
            const userOrganisations = await userOrganisationsApi.getUserOrganisations(user_id);

            return {
                user: {
                    id: user_id,
                    role, // Возвращаем исходную роль (admin), преобразование будет в fulfilled
                    firstName,
                    lastName
                },
                userOrganisations
            };
        } catch (err) {
            return rejectWithValue(err.message);
        }
    }
);

// Логин
export const loginUser = createAsyncThunk(
    'auth/login',
    async ({ username, password }, { rejectWithValue }) => {
        try {
            const { access_token, refresh_token, id, role, first_name, last_name } =
                await authApi.login(username, password);

            // Сохраняем в localStorage ИСХОДНУЮ роль из API (не преобразованную)
            localStorage.setItem('access_token', access_token);
            localStorage.setItem('refresh_token', refresh_token);
            localStorage.setItem('user_id', id);
            localStorage.setItem('user_role', role); // Сохраняем исходную роль (admin)
            localStorage.setItem('first_name', first_name);
            localStorage.setItem('last_name', last_name);

            // Подтверждение авторизации
            await authApi.getProtectedData();
            const userOrganisations = await userOrganisationsApi.getUserOrganisations(id);

            return {
                user: {
                    id,
                    role,
                    firstName: first_name,
                    lastName: last_name
                },
                userOrganisations
            };
        } catch (err) {
            return rejectWithValue(err.message || 'Login failed');
        }
    }
);

// Регистрация
export const registerUser = createAsyncThunk(
    'auth/register',
    async (userData, { rejectWithValue }) => {
        try {
            return await authApi.register(userData);
        } catch (err) {
            return rejectWithValue(err.message || 'Registration failed');
        }
    }
);


// Приоритет ролей (чем выше число, тем выше приоритет)
const ROLE_PRIORITY = {
    'hr': 4,
    'admin': 3,
    'leader': 2,
    'employee': 1
};

// Функция для выбора главной роли в организации
const selectPrimaryRole = (userOrganisations, orgId) => {
    const orgRoles = userOrganisations.filter(org => org.organisation_id === orgId);

    if (orgRoles.length === 0) return null;
    if (orgRoles.length === 1) return orgRoles[0];

    // Если несколько ролей - выбираем с наивысшим приоритетом
    return orgRoles.reduce((prev, current) => {
        const prevPriority = ROLE_PRIORITY[prev.role] || 0;
        const currentPriority = ROLE_PRIORITY[current.role] || 0;
        return currentPriority > prevPriority ? current : prev;
    });
};

const initialState = {
    user: null,
    isAuth: false,
    orgId: null,
    userOrganisations: [],
    isLoading: true,
    error: null,
    role: null, // 'superadmin' или роль из userOrganisations ('hr', 'admin', 'leader', 'employee')
    departmentId: null // department_id для роли leader
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setOrgId(state, action) {
            const newOrgId = action.payload;
            state.orgId = newOrgId;

            // Проверяем, является ли пользователь superadmin (по исходной роли из localStorage или текущему state)
            const storedRole = localStorage.getItem('user_role');
            const isSuperadmin = storedRole === 'superadmin' || state.role === 'superadmin';

            // Обновляем role в зависимости от организации
            if (!isSuperadmin) {
                // Если не superadmin - ищем роль для выбранной организации
                if (newOrgId && state.userOrganisations.length > 0) {
                    const orgData = selectPrimaryRole(state.userOrganisations, newOrgId);
                    state.role = orgData?.role || null;
                    state.departmentId = orgData?.department_id || null;
                } else {
                    state.role = null;
                    state.departmentId = null;
                }
            } else {
                // Если superadmin - роль остается 'superadmin', departmentId не нужен
                state.role = 'superadmin';
                state.departmentId = null;
            }

            if (newOrgId) {
                localStorage.setItem('organisation_id', String(newOrgId));
            } else {
                localStorage.removeItem('organisation_id');
            }
        },
        logout(state) {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('user_id');
            localStorage.removeItem('user_role');
            localStorage.removeItem('first_name');
            localStorage.removeItem('last_name');
            localStorage.removeItem('organisation_id');
            localStorage.removeItem('showAllVacancies');
            localStorage.removeItem('showAllRequests');
            state.user = null;
            state.isAuth = false;
            state.error = null;
            state.orgId = null;
            state.userOrganisations = [];
            state.role = null;
            state.departmentId = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // checkAuth
            .addCase(checkAuth.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(checkAuth.fulfilled, (state, action) => {
                state.user = action.payload.user;
                state.isAuth = true;
                state.isLoading = false;

                const organisations = action.payload.userOrganisations || [];
                state.userOrganisations = organisations;
                const persisted = localStorage.getItem('organisation_id');
                const persistedId = persisted ? Number(persisted) : null;
                const hasPersisted =
                    persistedId !== null && Number.isFinite(persistedId) &&
                    organisations.some(o => (o.organisation_id ?? o.id) === persistedId);
                const firstOrg = organisations[0] || null;
                const defaultOrgId = firstOrg ? (firstOrg.organisation_id ?? firstOrg.id ?? null) : null;
                const chosenOrgId = hasPersisted ? persistedId : defaultOrgId;
                state.orgId = chosenOrgId;

                // Устанавливаем роль (преобразуем admin -> superadmin)
                // Проверяем исходную роль из API ответа или из localStorage
                const userRoleFromApi = action.payload.user.role;
                const storedRole = localStorage.getItem('user_role');
                
                // Определяем, является ли пользователь superadmin
                // Если в localStorage уже есть 'superadmin' ИЛИ исходная роль из API 'admin' - это superadmin
                const isSuperadmin = storedRole === 'superadmin' || userRoleFromApi === 'admin';
                
                if (isSuperadmin) {
                    state.role = 'superadmin';
                    state.departmentId = null;
                    localStorage.setItem('user_role', 'superadmin');
                } else if (chosenOrgId && organisations.length > 0) {
                    const orgData = selectPrimaryRole(organisations, chosenOrgId);
                    state.role = orgData?.role || null;
                    state.departmentId = orgData?.department_id || null;
                    localStorage.setItem('user_role', orgData?.role || null);
                } else {
                    state.role = null;
                    state.departmentId = null;
                    localStorage.setItem('user_role', null);
                }

                if (chosenOrgId !== null && chosenOrgId !== undefined) {
                    localStorage.setItem('organisation_id', String(chosenOrgId));
                } else {
                    localStorage.removeItem('organisation_id');
                }
            })
            .addCase(checkAuth.rejected, (state) => {
                console.log('checkAuth rejected');
                state.user = null;
                state.isAuth = false;
                state.isLoading = false;
            })

            // login
            .addCase(loginUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.user = action.payload.user;
                state.isAuth = true;
                state.isLoading = false;

                const organisations = action.payload.userOrganisations || [];
                state.userOrganisations = organisations;
                const persisted = localStorage.getItem('organisation_id');
                const persistedId = persisted ? Number(persisted) : null;
                const hasPersisted =
                    persistedId !== null && Number.isFinite(persistedId) &&
                    organisations.some(o => (o.organisation_id ?? o.id) === persistedId);
                const firstOrg = organisations[0] || null;
                const defaultOrgId = firstOrg ? (firstOrg.organisation_id ?? firstOrg.id ?? null) : null;
                const chosenOrgId = hasPersisted ? persistedId : defaultOrgId;
                state.orgId = chosenOrgId;

                // Устанавливаем роль (преобразуем admin -> superadmin)
                // Проверяем исходную роль из API ответа или из localStorage
                const userRoleFromApi = action.payload.user.role;
                const storedRole = localStorage.getItem('user_role');
                
                // Определяем, является ли пользователь superadmin
                // Если в localStorage уже есть 'superadmin' ИЛИ исходная роль из API 'admin' - это superadmin
                const isSuperadmin = storedRole === 'superadmin' || userRoleFromApi === 'admin';
                
                if (isSuperadmin) {
                    state.role = 'superadmin';
                    state.departmentId = null;
                    localStorage.setItem('user_role', 'superadmin');
                } else if (chosenOrgId && organisations.length > 0) {
                    const orgData = selectPrimaryRole(organisations, chosenOrgId);
                    state.role = orgData?.role || null;
                    state.departmentId = orgData?.department_id || null;
                    localStorage.setItem('user_role', orgData?.role || null);
                } else {
                    state.role = null;
                    state.departmentId = null;
                    localStorage.setItem('user_role', null);
                }

                if (chosenOrgId !== null && chosenOrgId !== undefined) {
                    localStorage.setItem('organisation_id', String(chosenOrgId));
                } else {
                    localStorage.removeItem('organisation_id');
                }
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // register
            .addCase(registerUser.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(registerUser.fulfilled, (state) => {
                state.isLoading = false;
            })
            .addCase(registerUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

    }
});

export const { logout, setOrgId } = authSlice.actions;
export const authReducer = authSlice.reducer